import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { showNotification } from '@mantine/notifications';
import {
  delegateUser,
  DelegateUserBody,
  getMyDelegations,
  listDelegableEvents,
  listDelegations,
  listUsers,
  ListUsersParams,
  removeDelegation,
  updateDelegation,
  updateUserAccess,
} from '@/services/api/userControl/userControl.service';
import { AccessStatus, StaffRole, UserRole } from '@/types/userControl';

const USERS_KEY = 'adminUsers';
const STAFF_KEY = ['eventStaff'];
const STAFF_EVENTS_KEY = ['eventStaffEvents'];

const errorMessage = (error: AxiosError) =>
  (error.response?.data as { message?: string } | undefined)?.message ?? error.message;

const notifyError = (title: string) => (error: AxiosError) =>
  showNotification({ color: 'red', title, message: errorMessage(error) });

const notifySuccess = (title: string) =>
  showNotification({ color: 'green', title, message: 'Alteração salva com sucesso.' });

export const useAdminUsers = (params: ListUsersParams, enabled: boolean) =>
  useQuery({
    queryKey: [USERS_KEY, params],
    queryFn: () => listUsers(params),
    enabled,
    placeholderData: keepPreviousData,
  });

export const useUpdateUserAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      role?: UserRole;
      status?: AccessStatus;
      emailVerified?: boolean;
    }) => updateUserAccess(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
      notifySuccess('Usuário atualizado');
    },
    onError: notifyError('Não foi possível atualizar o usuário'),
  });
};

export const useDelegations = () => useQuery({ queryKey: STAFF_KEY, queryFn: listDelegations });

/** Onde o usuário logado está na "Equipe dos eventos" (libera tabela de eventos e painel). */
export const useMyDelegations = (enabled: boolean) =>
  useQuery({ queryKey: ['myDelegations'], queryFn: getMyDelegations, enabled, staleTime: 60_000 });

export const useDelegableEvents = () =>
  useQuery({ queryKey: STAFF_EVENTS_KEY, queryFn: listDelegableEvents });

export const useDelegateUser = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DelegateUserBody) => delegateUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STAFF_KEY });
      notifySuccess('Usuário delegado');
      onSuccess();
    },
    onError: notifyError('Não foi possível delegar o usuário'),
  });
};

export const useUpdateDelegation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; role?: StaffRole; status?: AccessStatus }) =>
      updateDelegation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STAFF_KEY });
      notifySuccess('Delegação atualizada');
    },
    onError: notifyError('Não foi possível atualizar a delegação'),
  });
};

export const useRemoveDelegation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removeDelegation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STAFF_KEY });
      notifySuccess('Delegação removida');
    },
    onError: notifyError('Não foi possível remover a delegação'),
  });
};
